import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Drawer, Button, message } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import AccessProfileForm, { AccessProfileFormValues } from '../../components/AccessProfileForm';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import styles from './CreateAccessProfile.module.css';

const CreateAccessProfile = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');
  const enableNewApproverProfile = config.data?.t010_enableNewApproverProfile;

  const mutation = useAPIMutation(
    async (
      {
        name,
        viewAllVacancies,
        createAndEditVacancies,
        accessTalentPool,
        accessConfigurations,
        accessReports,
        viewRequestJobPosting,
        createAndEditRequestJobPosting,
        canApproveRequestJobPosting,
        approveRequestJobPostingPermission,
      }: AccessProfileFormValues,
      api
    ) => {
      const permissions = (
        await api.get('/permissions/', {
          params: {
            codename__in: [
              'view_all_vacancies',
              'create_and_edit_vacancies',
              'access_talent_pool',
              'access_configurations',
              'access_reports',
              'view_marked_request_job_postings',
              'view_all_request_job_postings',
              'create_and_edit_request_job_postings',
              'can_approve_marked_request_job_postings',
              'can_approve_all_request_job_postings',
            ].join(','),
          },
        })
      ).data;

      const codenameIds: Record<string, number> = {};
      for (const perm of permissions.results) {
        codenameIds[perm.codename] = perm.id;
      }

      return api.post('/groups/', {
        name,
        permissions: [
          ...(viewAllVacancies ? [codenameIds['view_all_vacancies']] : []),
          ...(createAndEditVacancies ? [codenameIds['create_and_edit_vacancies']] : []),
          ...(accessTalentPool ? [codenameIds['access_talent_pool']] : []),
          ...(accessConfigurations ? [codenameIds['access_configurations']] : []),
          ...(accessReports ? [codenameIds['access_reports']] : []),
          ...(viewRequestJobPosting === 'MARKED'
            ? [codenameIds['view_marked_request_job_postings']]
            : []),
          ...(viewRequestJobPosting === 'ALL'
            ? [codenameIds['view_all_request_job_postings']]
            : []),
          ...(createAndEditRequestJobPosting
            ? [codenameIds['create_and_edit_request_job_postings']]
            : []),
          ...(canApproveRequestJobPosting && !enableNewApproverProfile
            ? [codenameIds['can_approve_marked_request_job_postings']]
            : []),
          ...(approveRequestJobPostingPermission === 'MARKED' && enableNewApproverProfile
            ? [codenameIds['can_approve_marked_request_job_postings']]
            : []),
          ...(approveRequestJobPostingPermission === 'ALL' && enableNewApproverProfile
            ? [codenameIds['can_approve_all_request_job_postings']]
            : []),
        ],
      });
    },
    {
      onSuccess: (response) => {
        message.success(t('Perfil de acesso adicionado.'));
        navigate({
          pathname: '/:tenant/settings/accessManagement/accessProfiles/:access_profile_id/',
          params: { tenant, access_profile_id: response.data.id },
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/groups/');
        queryClient.invalidateQueries('/permissions/');
      },
      onError: (e, variables, context) => {
        if (
          e.response?.data.name &&
          Array.isArray(e.response?.data.name) &&
          e.response?.data.name.length > 0 &&
          (e.response?.data.name[0] === 'grupo com este nome já existe.' ||
            e.response.data.name[0] === 'group with this name already exists.') // TODO: POG TEMPORARIO ATÉ ARRUMAR OS ERROS
        ) {
          message.error(t('Já existe um perfil com esse nome.'));
          return;
        }
        queryClient.defaultMutationOptions().onError?.(e, variables, context);
      },
    }
  );

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  return (
    <Drawer
      className={styles.drawer}
      title={
        <PageTitle
          title={t('Novo perfil')}
          level={2}
          extra={[
            <Button
              key="close"
              type="text"
              onClick={() =>
                navigate({
                  pathname: '/:tenant/settings/accessManagement/accessProfiles/',
                  params: { tenant },
                })
              }
              shape="circle"
            >
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
      maskClosable
      size="large"
      closable={false}
      width={720}
      footer={
        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={mutation.isLoading}
          onClick={() => form.submit()}
        >
          {' '}
          <Trans>Adicionar</Trans>
        </Button>
      }
      open
      onClose={() =>
        navigate({
          pathname: '/:tenant/settings/accessManagement/accessProfiles/',
          params: { tenant },
        })
      }
    >
      <Helmet>
        <title>
          {t('Novo perfil')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <AccessProfileForm
        form={form}
        layout="vertical"
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      />
    </Drawer>
  );
};

export default withRouteChange()(CreateAccessProfile);
